import React from "react"
import { Switch, Popconfirm, Dropdown, Menu } from "antd"
import Icon from "../../Icon"
import Strings from "utils/strings"

import { ActionsType } from ".."

const stopPropagation = (
	e: React.MouseEvent<HTMLElement, MouseEvent> | undefined
) => {
	e?.stopPropagation()
}

const stopPropagationOnClick =
	(onClick: Function, disabled?: boolean) =>
	(e: React.MouseEvent<HTMLElement, MouseEvent> | undefined) => {
		e?.stopPropagation()
		if (typeof onClick === "function" && !disabled) onClick()
	}

const disabledStyle = {
	opacity: 0.5,
	cursor: "not-allowed",
}

export const View = ({ onClick, disabled }: any) => (
	<div
		className="ActionOption"
		onClick={stopPropagationOnClick(onClick, disabled)}
		style={disabled ? disabledStyle : {}}
	>
		<Icon name="eye" />
	</div>
)

export const ViewRoute = ({ onClick, disabled }: any) => (
	<div
		className="ActionOption"
		onClick={stopPropagationOnClick(onClick, disabled)}
		style={disabled ? disabledStyle : {}}
	>
		<Icon name="route" />
	</div>
)

export const Edit = ({ onClick, disabled, hide = false }: any) => {
	if (hide) return null
	return (
		<div
			className="ActionOption"
			onClick={stopPropagationOnClick(onClick, disabled)}
			style={disabled ? disabledStyle : {}}
		>
			<Icon name="pencil-outline" />
		</div>
	)
}

export const Export = ({ onClick, disabled, hide = false }: any) => {
	if (hide) return null
	return (
		<div
			className="ActionOption"
			onClick={stopPropagationOnClick(onClick, disabled)}
			style={disabled ? disabledStyle : {}}
		>
			<Icon name="inbox" />
		</div>
	)
}

export const Remove = ({ onClick, disabled }: any) => {
	if (disabled) {
		return (
			<div
				className="ActionOption"
				onClick={stopPropagation}
				style={disabledStyle}
			>
				<Icon name="trash" />
			</div>
		)
	}

	return (
		<Popconfirm
			title={Strings.orders.archiveMessage}
			okText={Strings.orders.archive}
			cancelText={Strings.generic.cancel}
			okType="danger"
			placement="topRight"
			onConfirm={onClick}
			onCancel={stopPropagation}
		>
			<div className="ActionOption" onClick={stopPropagation}>
				<Icon name="trash" />
			</div>
		</Popconfirm>
	)
}

export const Delete = ({ onClick, disabled }: any) => {
	if (disabled) {
		return (
			<div
				className="ActionOption"
				onClick={stopPropagation}
				style={disabledStyle}
			>
				<Icon name="trash" />
			</div>
		)
	}

	return (
		<Popconfirm
			title={Strings.generic.deleteMessage}
			okText={Strings.generic.delete}
			cancelText={Strings.generic.cancel}
			okType="danger"
			placement="topRight"
			onConfirm={onClick}
			onCancel={stopPropagation}
		>
			<div className="ActionOption" onClick={stopPropagation}>
				<Icon name="trash" />
			</div>
		</Popconfirm>
	)
}

export const Toggle = ({ value, onChange, disabled }: any) => (
	<div
		className="ActionSwitch"
		onClick={stopPropagation}
		style={disabled ? disabledStyle : {}}
	>
		<Switch
			size="small"
			checked={Boolean(value)}
			disabled={disabled}
			onChange={onChange}
		/>
	</div>
)

export const ExportData = ({ functions, disabled }: any) => {
	if (disabled) {
		return (
			<div
				className="ActionOption"
				onClick={stopPropagation}
				style={disabledStyle}
			>
				<Icon name="inbox" />
			</div>
		)
	}

	// If only one export option, execute it directly without dropdown
	if (functions && functions.length === 1) {
		return (
			<div
				className="ActionOption"
				onClick={stopPropagationOnClick(functions[0].onClick)}
			>
				<Icon name="inbox" />
			</div>
		)
	}

	// Multiple export options - show dropdown
	const handleMenuClick = (e: any) => {
		e.domEvent?.stopPropagation()
		const selectedFunc = functions[parseInt(e.key)]
		if (selectedFunc && typeof selectedFunc.onClick === "function") {
			selectedFunc.onClick()
		}
	}

	const menu = (
		<Menu onClick={handleMenuClick}>
			{functions.map((func: any, index: number) => (
				<Menu.Item key={index}>
					{func.icon && <Icon name={func.icon} style={{ marginRight: 8 }} />}
					{func.label}
				</Menu.Item>
			))}
		</Menu>
	)

	return (
		<div className="ActionOption" onClick={stopPropagation}>
			<Dropdown overlay={menu} placement="bottomRight" trigger={["click"]}>
				<div
					style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
				>
					<Icon name="inbox" />
				</div>
			</Dropdown>
		</div>
	)
}

export class Actions extends React.PureComponent<ActionsType & { value: any }> {
	get hasView() {
		const { view } = this.props
		return typeof view === "function"
	}

	get hasViewRoute() {
		const { viewRoute } = this.props
		return typeof viewRoute === "function"
	}

	get hasEdit() {
		const { edit } = this.props
		return typeof edit === "function"
	}

	get hasDelete() {
		const { remove } = this.props
		return typeof remove === "function"
	}

	get hasDelete2() {
		const { deleteAction } = this.props
		return typeof deleteAction === "function"
	}

	get hasExport() {
		const { exportExcel } = this.props
		return typeof exportExcel === "function"
	}

	get hasToggle() {
		const { toggle } = this.props
		return typeof toggle === "function"
	}

	get hasExportData() {
		const { exportData } = this.props
		return typeof exportData === "function"
	}

	getEditProps() {
		const { edit, value } = this.props
		// @ts-ignore
		return edit(value.original, value)
	}

	getViewProps() {
		const { view, value } = this.props
		// @ts-ignore
		return view(value.original, value)
	}

	getViewRouteProps() {
		const { viewRoute, value } = this.props
		// @ts-ignore
		return viewRoute(value.original, value)
	}

	getRemoveProps() {
		const { remove, value } = this.props
		// @ts-ignore
		return remove(value.original, value)
	}

	getDeleteProps() {
		const { deleteAction, value } = this.props
		// @ts-ignore
		return deleteAction(value.original, value)
	}

	getExportProps() {
		const { exportExcel, value } = this.props
		// @ts-ignore
		return exportExcel(value.original, value)
	}

	getToggleProps() {
		const { toggle, value } = this.props
		// @ts-ignore
		return toggle(value.original, value)
	}

	getExportDataProps() {
		const { exportData, value } = this.props
		// @ts-ignore
		return exportData(value.original, value)
	}

	render() {
		return (
			<React.Fragment>
				{this.hasEdit && <Edit {...this.getEditProps()} />}
				{this.hasView && <View {...this.getViewProps()} />}
				{this.hasViewRoute && <ViewRoute {...this.getViewRouteProps()} />}
				{this.hasExport && <Export {...this.getExportProps()} />}
				{this.hasDelete && <Remove {...this.getRemoveProps()} />}
				{this.hasDelete2 && <Delete {...this.getDeleteProps()} />}
				{this.hasToggle && <Toggle {...this.getToggleProps()} />}
				{this.hasExportData && <ExportData {...this.getExportDataProps()} />}
			</React.Fragment>
		)
	}
}
